/**
 * RealmIntro — produced intro video that plays ONCE per student, the first
 * time they start a battle in a realm. (This trigger works for every realm,
 * including the ones open from the first login — unlike the unlock-crossing
 * cinematic, which only fires for realms that start locked.)
 *
 * "First time" = the student has no completed attempt in the realm (from their
 * server-side attempt history, so it follows them across devices), plus a
 * localStorage mark so repeatedly abandoning the first battle doesn't replay
 * the video every click. GameWorld owns that decision; this component just
 * plays the registered video for a biome.
 *
 * ── Adding a video for another realm ─────────────────────────────────────────
 * 1. Drop the master file in src/media/ (e.g. civics-realm.mp4 — kept out of
 *    the bundle) and generate the two web encodes beside it:
 *      ffmpeg -i X.mp4 -map 0:v:0 -map 0:a:0 -c:v libx264 -preset slow -crf 27 \
 *        -pix_fmt yuv420p -movflags +faststart -c:a aac -b:a 96k X.web.mp4
 *      ffmpeg -i X.mp4 -map 0:v:0 -map 0:a:0 -c:v libvpx-vp9 -crf 38 -b:v 0 \
 *        -row-mt 1 -pix_fmt yuv420p -c:a libopus -b:a 64k X.web.webm
 * 2. Import both below and add a REALM_INTROS entry for the biome.
 * Nothing else — the trigger, once-only logic, and preview all key off the map.
 *
 * Preview any intro with ?intro=<biome> (e.g. /play?intro=history).
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import historyRealmMp4 from '@/media/history-realm.web.mp4'
import historyRealmWebm from '@/media/history-realm.web.webm'
import geographyRealmMp4 from '@/media/geography-realm.web.mp4'
import geographyRealmWebm from '@/media/geography-realm.web.webm'
import economicRealmMp4 from '@/media/economic-realm.web.mp4'
import economicRealmWebm from '@/media/economic-realm.web.webm'
import civicsRealmMp4 from '@/media/civics-realm.web.mp4'
import civicsRealmWebm from '@/media/civics-realm.web.webm'
import worldIntroMp4 from '@/media/world-intro.web.mp4'
import worldIntroWebm from '@/media/world-intro.web.webm'

interface IntroSources { webm: string; mp4: string; label: string; accent: string }

export const REALM_INTROS: Record<string, IntroSources> = {
  geography: { webm: geographyRealmWebm, mp4: geographyRealmMp4,
               label: 'GEOGRAPHY FRONTIER', accent: '#7adb7a' },
  history:   { webm: historyRealmWebm, mp4: historyRealmMp4,
               label: 'HISTORY REALM', accent: '#e9c878' },
  economics: { webm: economicRealmWebm, mp4: economicRealmMp4,
               label: 'ECO EXCHANGE', accent: '#f0926e' },
  civics:    { webm: civicsRealmWebm, mp4: civicsRealmMp4,
               label: 'CIVICS CAPITOL', accent: '#c89ee6' },
  world:     { webm: worldIntroWebm, mp4: worldIntroMp4,
               label: 'SUPER CIVICA WORLD', accent: '#ffd840' },
}

/** True if this biome has an intro video registered. */
export function hasRealmIntro(biome: string | undefined | null): boolean {
  return !!(biome && REALM_INTROS[biome])
}

export default function RealmIntro({ biome, onDone }: { biome: string; onDone: () => void }) {
  const src = REALM_INTROS[biome]
  const videoRef  = useRef<HTMLVideoElement>(null)
  const doneRef   = useRef(false)
  const [started, setStarted]         = useState(false)
  const [skippable, setSkippable]     = useState(false)
  const [needsUnmute, setNeedsUnmute] = useState(false)
  // When the first tap is spent unmuting, it must not ALSO skip the video.
  const unmuteConsumedClickRef = useRef(false)

  const finish = useCallback(() => {
    if (doneRef.current) return
    doneRef.current = true
    onDone()
  }, [onDone])

  // Sound-on is the default. If the browser forced a muted start (no user
  // gesture yet — e.g. the ?intro= preview URL on a fresh load), the very
  // first tap or keypress anywhere unmutes; that input is consumed and never
  // skips.
  useEffect(() => {
    if (!needsUnmute) return
    const onFirstInput = () => {
      const v = videoRef.current
      if (v) v.muted = false
      unmuteConsumedClickRef.current = true
      setNeedsUnmute(false)
    }
    window.addEventListener('pointerdown', onFirstInput, { capture: true })
    window.addEventListener('keydown', onFirstInput, { capture: true })
    return () => {
      window.removeEventListener('pointerdown', onFirstInput, { capture: true })
      window.removeEventListener('keydown', onFirstInput, { capture: true })
    }
  }, [needsUnmute])

  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    // Prefer sound (the student just clicked a boss, so a user gesture exists);
    // if the browser refuses anyway, retry muted and show the unmute cue.
    v.play().then(() => setStarted(true)).catch(() => {
      v.muted = true
      v.play().then(() => { setStarted(true); setNeedsUnmute(true) }).catch(finish)
    })
    const grace = setTimeout(() => setSkippable(true), 2000)
    // Safety net: a stalled download must never trap a student on black.
    const cap = setTimeout(finish, 30_000)
    return () => { clearTimeout(grace); clearTimeout(cap) }
  }, [finish])

  if (!src) { onDone(); return null }   // unregistered biome — never block play

  return (
    <div
      onClick={() => {
        // The tap that unmuted must not also skip
        if (unmuteConsumedClickRef.current) { unmuteConsumedClickRef.current = false; return }
        if (skippable) finish()
      }}
      style={{
        position: 'fixed', inset: 0, zIndex: 90, background: '#000',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: skippable ? 'pointer' : 'default',
        fontFamily: '"Press Start 2P", monospace',
      }}
    >
      <video
        ref={videoRef}
        playsInline
        preload="auto"
        onEnded={finish}
        onError={finish}
        style={{
          width: '100%', height: '100%', objectFit: 'contain',
          opacity: started ? 1 : 0, transition: 'opacity .6s ease',
        }}
      >
        <source src={src.webm} type="video/webm"/>
        {/* Last source's error = no playable format → just proceed to battle */}
        <source src={src.mp4} type="video/mp4" onError={finish}/>
      </video>
      {/* Eyebrow so the moment is labeled even before the video fades in */}
      <div style={{
        position: 'absolute', top: 34, left: 0, right: 0, textAlign: 'center',
        fontSize: 13, letterSpacing: '3px', color: src.accent,
        textShadow: '0 2px 0 #000', pointerEvents: 'none',
        opacity: started ? 1 : 0.6, transition: 'opacity .6s ease',
      }}>
        ✦ NOW ENTERING · {src.label} ✦
      </div>
      {/* Autoplay had to start muted → any tap/keypress restores sound */}
      {needsUnmute && started && (
        <div style={{
          position: 'absolute', top: 70, right: 24,
          fontSize: 10, letterSpacing: '1px',
          color: '#ffd840', background: 'rgba(0,0,0,0.65)',
          border: '2px solid #ffd840', borderRadius: 8,
          padding: '10px 14px', pointerEvents: 'none',
        }}>
          🔊 TAP ANYWHERE FOR SOUND
        </div>
      )}
      {skippable && (
        <div style={{
          position: 'absolute', bottom: 30, left: 0, right: 0, textAlign: 'center',
          color: 'rgba(255,255,255,0.75)', fontSize: 10, letterSpacing: '2px',
          textShadow: '0 2px 0 #000', pointerEvents: 'none',
        }}>
          ▶ TAP TO SKIP
        </div>
      )}
    </div>
  )
}
